import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  finkAssistantService,
  ChatMessage,
  ChatAction,
  ChatRequest,
  ChatMode,
  DeepDiveContext,
  ChecklistItem,
} from '@/services/finkAssistant.service';

export interface SelectedInsightsClient {
  clientId: string;
  clientName: string;
  panId: string;
}

export interface SelectedInsight {
  index: number;
  text: string;
}

interface FinkAssistantState {
  isOpen: boolean;
  isMinimized: boolean;
  isFullScreen: boolean;
  messages: ChatMessage[];
  pendingActions: ChatAction[];
  isLoading: boolean;
  isStreaming: boolean;  // True while streaming text
  error: string | null;
  selectedInsightsClient: SelectedInsightsClient | null;
  // Deep Dive Mode
  chatMode: ChatMode;
  deepDiveContext: DeepDiveContext | null;
  actionChecklist: ChecklistItem[];
  // Insight Analysis Mode
  selectedInsight: SelectedInsight | null;
}

const initialState: FinkAssistantState = {
  isOpen: false,
  isMinimized: false,
  isFullScreen: false,
  messages: [],
  pendingActions: [],
  isLoading: false,
  isStreaming: false,
  error: null,
  selectedInsightsClient: null,
  // Deep Dive Mode
  chatMode: 'general',
  deepDiveContext: null,
  actionChecklist: [],
  // Insight Analysis Mode
  selectedInsight: null,
};

export const sendMessage = createAsyncThunk(
  'finkAssistant/sendMessage',
  async (
    { message, context }: { message: string; context?: { currentPage?: string; deepDive?: DeepDiveContext } },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { finkAssistant: FinkAssistantState };
      // Get messages BEFORE the current user message was added
      // The current message is already in state (added by addUserMessage dispatch)
      // So we need to exclude it to avoid duplication since we send it separately
      const allMessages = state.finkAssistant.messages;
      const conversationHistory = allMessages.slice(0, -1); // Exclude the last message (current user message)

      // Include deep dive context from state if not explicitly provided
      const deepDiveContext = context?.deepDive || state.finkAssistant.deepDiveContext;

      const request: ChatRequest = {
        message,
        conversationHistory,
        context: {
          ...context,
          deepDive: deepDiveContext || undefined,
        },
      };

      const response = await finkAssistantService.chat(request);
      return response;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      return rejectWithValue(
        err.response?.data?.error || err.message || 'Failed to send message'
      );
    }
  }
);

const finkAssistantSlice = createSlice({
  name: 'finkAssistant',
  initialState,
  reducers: {
    openChat: (state) => {
      state.isOpen = true;
      state.isMinimized = false;
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    toggleChat: (state) => {
      if (state.isMinimized) {
        state.isMinimized = false;
      } else {
        state.isOpen = !state.isOpen;
      }
    },
    minimizeChat: (state) => {
      state.isMinimized = true;
    },
    expandChat: (state) => {
      state.isMinimized = false;
    },
    toggleFullScreen: (state) => {
      state.isFullScreen = !state.isFullScreen;
    },
    setFullScreen: (state, action: PayloadAction<boolean>) => {
      state.isFullScreen = action.payload;
    },
    addUserMessage: (state, action: PayloadAction<string>) => {
      state.messages.push({
        role: 'user',
        content: action.payload,
      });
    },
    clearMessages: (state) => {
      state.messages = [];
      state.pendingActions = [];
    },
    clearActions: (state) => {
      state.pendingActions = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedInsightsClient: (state, action: PayloadAction<SelectedInsightsClient>) => {
      state.selectedInsightsClient = action.payload;
    },
    clearSelectedInsightsClient: (state) => {
      state.selectedInsightsClient = null;
    },
    // Insight Analysis Actions
    setSelectedInsight: (state, action: PayloadAction<SelectedInsight>) => {
      state.selectedInsight = action.payload;
    },
    clearSelectedInsight: (state) => {
      state.selectedInsight = null;
    },
    // Open chat with insight analysis - triggers auto-send in FinkChatWidget
    openChatWithInsight: (state, action: PayloadAction<SelectedInsight>) => {
      state.isOpen = true;
      state.isMinimized = false;
      state.selectedInsight = action.payload;
      state.messages = []; // Start fresh for insight analysis
      state.pendingActions = [];
      state.error = null;
      state.chatMode = 'general'; // Use general mode for insights
    },
    // Deep Dive Mode Actions
    startDeepDive: (state, action: PayloadAction<DeepDiveContext>) => {
      state.isOpen = true;
      state.isMinimized = false;
      state.chatMode = 'deepDive';
      state.deepDiveContext = action.payload;
      state.messages = []; // Start fresh for deep dive
      state.pendingActions = [];
      state.actionChecklist = [];
      state.error = null;
    },
    exitDeepDive: (state) => {
      state.chatMode = 'general';
      state.deepDiveContext = null;
      state.actionChecklist = [];
      // Keep messages so user can continue chatting
    },
    updateChecklist: (state, action: PayloadAction<ChecklistItem[]>) => {
      state.actionChecklist = action.payload;
    },
    toggleChecklistItem: (state, action: PayloadAction<string>) => {
      const item = state.actionChecklist.find(i => i.id === action.payload);
      if (item) {
        item.status = item.status === 'pending' ? 'completed' : 'pending';
      }
    },
    // Streaming Actions
    startStreaming: (state) => {
      state.isStreaming = true;
      state.isLoading = true;
      state.error = null;
      state.pendingActions = [];
      // Add empty assistant message placeholder for streaming
      state.messages.push({
        role: 'assistant',
        content: '',
      });
    },
    appendToLastMessage: (state, action: PayloadAction<string>) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant') {
        lastMessage.content += action.payload;
      }
    },
    finishStreaming: (state, action: PayloadAction<{ actions?: ChatAction[]; metadata?: { checklist?: ChecklistItem[] } }>) => {
      state.isStreaming = false;
      state.isLoading = false;
      if (action.payload.actions) {
        state.pendingActions = action.payload.actions;
      }
      if (action.payload.metadata?.checklist) {
        state.actionChecklist = action.payload.metadata.checklist;
      }
    },
    streamError: (state, action: PayloadAction<string>) => {
      state.isStreaming = false;
      state.isLoading = false;
      state.error = action.payload;
      // Remove the empty placeholder message on error
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
        state.messages.pop();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.pendingActions = [];
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseMessage = action.payload?.message || "I couldn't generate a response. Please try again.";
        state.messages.push({
          role: 'assistant',
          content: responseMessage,
        });
        if (action.payload?.actions) {
          state.pendingActions = action.payload.actions;
        }
        // Update action checklist from metadata if provided
        if (action.payload?.metadata?.checklist) {
          state.actionChecklist = action.payload.metadata.checklist;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  openChat,
  closeChat,
  toggleChat,
  minimizeChat,
  expandChat,
  toggleFullScreen,
  setFullScreen,
  addUserMessage,
  clearMessages,
  clearActions,
  clearError,
  setSelectedInsightsClient,
  clearSelectedInsightsClient,
  // Insight Analysis Actions
  setSelectedInsight,
  clearSelectedInsight,
  openChatWithInsight,
  // Deep Dive Mode Actions
  startDeepDive,
  exitDeepDive,
  updateChecklist,
  toggleChecklistItem,
  // Streaming Actions
  startStreaming,
  appendToLastMessage,
  finishStreaming,
  streamError,
} = finkAssistantSlice.actions;

export default finkAssistantSlice.reducer;
